import React from "react";
import { EMAIL_DESTINATIONS } from "./broadcastEnums";

/**
 * The link editor that drops under the body while a link is being written.
 *
 * Inline rather than a popover on purpose: a floating layer over a
 * `contentEditable` fights the caret for the same few hundred pixels, and the
 * author needs to see the sentence the link is going into while they type the
 * destination.
 *
 * Purely presentational — every value and every action is the caller's. It does
 * not know what a selection is; `EmailBodyEditor` owns all of that.
 */

const FIELD =
  "w-full rounded-lg border border-bc-border bg-white px-3 py-2.5 text-[13px] " +
  "text-bc-text placeholder:text-bc-faintest focus:outline-none " +
  "focus:border-bc-text transition-colors";

const CHIP =
  "rounded-full border border-bc-border bg-bc-page px-2.5 py-1 text-xs " +
  "text-bc-text-2 transition-colors hover:border-bc-dashed hover:bg-bc-neut-bg " +
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bc-soft";

const PRIMARY =
  "rounded-lg bg-bc-text px-4 py-2.5 text-[13px] font-medium text-white " +
  "transition-colors hover:bg-black disabled:opacity-40 " +
  "disabled:cursor-not-allowed focus-visible:outline-none " +
  "focus-visible:ring-2 focus-visible:ring-bc-text focus-visible:ring-offset-2";

const QUIET =
  "rounded-lg border border-bc-border bg-white px-3 py-2.5 text-[13px] " +
  "transition-colors hover:bg-bc-neut-bg focus-visible:outline-none " +
  "focus-visible:ring-2 focus-visible:ring-bc-soft";

/**
 * A row of one-click destinations.
 *
 * Shared by this panel and the button control below it — both are "put a URL in
 * that field", and offering the shortcut in one place but not the other would
 * read as an oversight.
 *
 * @param {Object} props
 * @param {(destination: {label: string, url: string}) => void} props.onPick
 * @param {string} [props.className]
 * @returns {React.ReactElement}
 */
export const DestinationChips = ({ onPick, className = "" }) => (
  <div className={`flex flex-wrap items-center gap-2 ${className}`}>
    <span className="w-full text-[11px] text-bc-soft">Common destinations</span>
    {EMAIL_DESTINATIONS.map((destination) => (
      <button
        key={destination.url}
        type="button"
        onClick={() => onPick(destination)}
        className={CHIP}
      >
        {destination.label}
      </button>
    ))}
  </div>
);

/**
 * Write or edit one link.
 *
 * @param {Object} props
 * @param {string} props.text - The link's visible label.
 * @param {string} props.url - Where it points.
 * @param {boolean} props.editing - True when an existing link in the body is being changed, which is what turns Insert into Update and reveals Remove.
 * @param {(value: string) => void} props.onTextChange
 * @param {(value: string) => void} props.onUrlChange
 * @param {() => void} props.onSubmit - Insert or update; the caller decides which.
 * @param {() => void} props.onRemove - Unwrap the link being edited.
 * @param {() => void} props.onCancel - Close without touching the body.
 * @returns {React.ReactElement}
 */
export const EmailLinkPanel = ({
  text,
  url,
  editing,
  onTextChange,
  onUrlChange,
  onSubmit,
  onRemove,
  onCancel,
}) => (
  <div
    data-testid="bc-email-link-panel"
    className="mt-2.5 rounded-[10px] border border-bc-text bg-white p-3.5"
  >
    <div className="flex flex-wrap items-center gap-2.5">
      <input
        type="text"
        value={text}
        placeholder="Link text"
        aria-label="Link text"
        onChange={(event) => onTextChange(event.target.value)}
        className={`${FIELD} min-w-[140px] flex-1`}
      />
      <input
        type="text"
        value={url}
        placeholder="https://flex.fit/…"
        aria-label="Link URL"
        onChange={(event) => onUrlChange(event.target.value)}
        className={`${FIELD} min-w-[180px] flex-[1.2] font-mono text-xs`}
      />
      <div className="flex shrink-0 items-center gap-2">
        {/* Disabled without a destination: an anchor with no href is just
            underlined text, and inserting one silently is worse than saying
            no. */}
        <button
          type="button"
          onClick={onSubmit}
          disabled={!url.trim()}
          className={PRIMARY}
        >
          {editing ? "Update" : "Insert"}
        </button>
        {editing ? (
          <button
            type="button"
            onClick={onRemove}
            className={`${QUIET} text-bc-bad`}
          >
            Remove
          </button>
        ) : null}
        <button
          type="button"
          onClick={onCancel}
          className={`${QUIET} text-bc-muted`}
        >
          Cancel
        </button>
      </div>
    </div>

    <DestinationChips
      className="mt-3"
      onPick={(destination) => {
        onUrlChange(destination.url);
        if (!text.trim()) onTextChange(destination.label);
      }}
    />
  </div>
);

export default EmailLinkPanel;
